import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, KABUPATEN_JATENG, JENIS_NARKOBA, STATUS_LABEL } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  FileText,
  AlertCircle,
  ShieldCheck,
  Hourglass,
  CheckCircle2,
  XCircle,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const STAT_CARDS = [
  { key: "total", label: "Total Laporan", Icon: FileText, color: "text-[#0B2B5E]", bg: "bg-[#0B2B5E]/5" },
  { key: "baru", label: "Baru Masuk", Icon: AlertCircle, color: "text-blue-700", bg: "bg-blue-50" },
  { key: "diverifikasi", label: "Diverifikasi", Icon: ShieldCheck, color: "text-amber-700", bg: "bg-amber-50" },
  { key: "dalam_penyelidikan", label: "Dalam Penyelidikan", Icon: Hourglass, color: "text-violet-700", bg: "bg-violet-50" },
  { key: "selesai", label: "Selesai", Icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50" },
  { key: "ditolak", label: "Ditolak", Icon: XCircle, color: "text-rose-700", bg: "bg-rose-50" },
];

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ status: "all", kabupaten: "all", jenis: "all", q: "" });
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [reports, setReports] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const loadStats = async () => {
    try {
      const res = await api.get("/reports/stats");
      setStats(res.data);
    } catch (_e) {
      /* ignore */
    }
  };

  const loadReports = async () => {
    setLoading(true);
    setErr("");
    try {
      const params = { page, limit };
      if (filters.status !== "all") params.status = filters.status;
      if (filters.kabupaten !== "all") params.kabupaten = filters.kabupaten;
      if (filters.jenis !== "all") params.jenis = filters.jenis;
      if (filters.q.trim()) params.q = filters.q.trim();
      const res = await api.get("/reports", { params });
      setReports(res.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Gagal memuat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.status, filters.kabupaten, filters.jenis]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(reports.total / limit)), [reports.total, limit]);

  const resolvedStats = useMemo(() => {
    const s = stats || { total: 0, by_status: {}, unread: 0 };
    return {
      total: s.total,
      baru: s.by_status?.baru || 0,
      diverifikasi: s.by_status?.diverifikasi || 0,
      dalam_penyelidikan: s.by_status?.dalam_penyelidikan || 0,
      selesai: s.by_status?.selesai || 0,
      ditolak: s.by_status?.ditolak || 0,
    };
  }, [stats]);

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      {/* HEADER */}
      <div>
        <div className="text-xs tracking-[0.22em] uppercase font-semibold text-slate-500">
          Ringkasan
        </div>
        <h1 className="mt-1 font-display font-black text-3xl tracking-tight text-[#0F172A]">
          Command Center Ditresnarkoba
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Pantau seluruh laporan yang masuk dari masyarakat di wilayah hukum Polda Jateng.
        </p>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {STAT_CARDS.map((c, i) => (
          <div
            key={c.key}
            data-testid={`stat-${c.key}`}
            className={`rounded-lg bg-white border border-slate-200 p-4 hover:-translate-y-0.5 hover:shadow-police transition-all reveal-up reveal-delay-${Math.min(i + 1, 4)}`}
          >
            <div className={`h-8 w-8 rounded-md ${c.bg} ${c.color} flex items-center justify-center mb-3`}>
              <c.Icon className="h-4 w-4" />
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-slate-500">
              {c.label}
            </div>
            <div className="mt-1 font-display font-black text-3xl text-[#0F172A] tabular-nums">
              {resolvedStats[c.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div className="rounded-lg bg-white border border-slate-200 p-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  loadReports();
                }
              }}
              data-testid="dashboard-search"
              placeholder="Cari nomor laporan, nama, deskripsi..."
              className="pl-9 h-10"
            />
          </div>
          <div className="min-w-[160px]">
            <Select
              value={filters.status}
              onValueChange={(v) => {
                setPage(1);
                setFilters((f) => ({ ...f, status: v }));
              }}
            >
              <SelectTrigger data-testid="filter-status" className="h-10">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                {Object.keys(STATUS_LABEL).map((k) => (
                  <SelectItem key={k} value={k} data-testid={`filter-status-${k}`}>
                    {STATUS_LABEL[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <Select
              value={filters.kabupaten}
              onValueChange={(v) => {
                setPage(1);
                setFilters((f) => ({ ...f, kabupaten: v }));
              }}
            >
              <SelectTrigger data-testid="filter-kabupaten" className="h-10">
                <SelectValue placeholder="Wilayah" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Wilayah</SelectItem>
                {KABUPATEN_JATENG.map((k) => (
                  <SelectItem key={k} value={k}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <Select
              value={filters.jenis}
              onValueChange={(v) => {
                setPage(1);
                setFilters((f) => ({ ...f, jenis: v }));
              }}
            >
              <SelectTrigger data-testid="filter-jenis" className="h-10">
                <SelectValue placeholder="Jenis Narkoba" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Jenis</SelectItem>
                {JENIS_NARKOBA.map((j) => (
                  <SelectItem key={j} value={j}>{j}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="font-display font-bold text-[#0F172A]">Daftar Laporan</div>
          <div className="text-xs text-slate-500">
            {reports.total} laporan ditemukan
          </div>
        </div>
        {err && (
          <div className="px-5 py-4 text-sm text-red-700 bg-red-50 border-b border-red-200">{err}</div>
        )}
        <div className="overflow-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Nomor</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Pelapor</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Jenis</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Wilayah</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Tanggal Masuk</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Status</TableHead>
                <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                    <Loader2 className="inline h-5 w-5 animate-spin mr-2" />
                    Memuat laporan...
                  </TableCell>
                </TableRow>
              )}
              {!loading && reports.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-500" data-testid="empty-reports">
                    Belum ada laporan yang sesuai filter
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                reports.items.map((r) => (
                  <TableRow
                    key={r.id}
                    data-testid={`report-row-${r.id}`}
                    className={`${!r.viewed ? "bg-blue-50/30" : ""}`}
                  >
                    <TableCell className="font-mono-tight font-bold text-[#0B2B5E]">
                      <div className="flex items-center gap-2">
                        {!r.viewed && (
                          <span
                            data-testid={`unread-dot-${r.id}`}
                            className="h-2 w-2 rounded-full bg-red-500"
                          />
                        )}
                        {r.nomor_laporan}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.pelapor_anonim ? (
                        <span className="text-slate-500 italic">Anonim</span>
                      ) : (
                        <span className="font-semibold text-[#0F172A]">
                          {r.nama_pelapor || "—"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{r.jenis_narkoba}</TableCell>
                    <TableCell className="text-sm text-slate-700">{r.kabupaten_kota}</TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(r.created_at)}</TableCell>
                    <TableCell>
                      <StatusBadge status={r.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to={`/admin/laporan/${r.id}`}
                        data-testid={`view-report-${r.id}`}
                        className="inline-flex items-center text-xs font-semibold text-[#0B2B5E] hover:underline"
                      >
                        Lihat Detail →
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
        {/* PAGINATION */}
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between text-sm">
          <div className="text-slate-500">
            Halaman {page} dari {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="pagination-prev"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Sebelumnya
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="pagination-next"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Selanjutnya
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
